import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import toast from "react-hot-toast";
import {
  Clock,
  MessageSquare,
  Mic,
  Save,
  ChevronDown,
  Check,
  Timer,
  Hourglass,
  Watch,
  Settings,
  Zap,
} from "lucide-react";

import { REST_API_URL } from "../../api/config";

const API = REST_API_URL;

const getUserTimeZone = () => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

const buildHeaders = (token, additionalHeaders = {}) => {
  const headers = {
    "Content-Type": "application/json",
    ...additionalHeaders,
  };
  
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }
  
  const timezone = getUserTimeZone();
  if (timezone) {
    headers["User-Timezone"] = timezone;
  }
  
  return headers;
};

export function BookingInformationCard() {
  const [choices, setChoices] = useState(null);
  const [duration, setDuration] = useState("0");
  const [customDuration, setCustomDuration] = useState("");
  const [responseTime, setResponseTime] = useState("24 hours");
  const [selectedTone, setSelectedTone] = useState(null);
  const [openDropdown, setOpenDropdown] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const token =
    localStorage.getItem("accessToken") ||
    sessionStorage.getItem("accessToken");

  useEffect(() => {
    (async () => {
      try {
        const headers = buildHeaders(token);
        
        const res = await fetch(`${API}/service/booking-choices/`, {
          headers,
        });
        const data = await res.json();
        setChoices(data);

        if (data?.tone_of_voice_options?.length > 0 && selectedTone === null) {
          setSelectedTone(data.tone_of_voice_options[0].id);
        }
      } catch {
        toast.error("Failed to load booking choices");
      }
    })();
  }, [token]);

  const durationOptions = [
    { label: "None", value: "0", icon: <Timer className="w-5 h-5" /> },
    { label: "15 minutes", value: "15", icon: <Clock className="w-5 h-5" /> },
    { label: "30 minutes", value: "30", icon: <Clock className="w-5 h-5" /> },
    { label: "1 hour", value: "60", icon: <Hourglass className="w-5 h-5" /> },
    { label: "2 hours", value: "120", icon: <Watch className="w-5 h-5" /> },
    { label: "Custom", value: "Other", icon: <Settings className="w-5 h-5" /> },
  ];

  const formatDuration = (m) => {
    m = +m;
    if (m === 0) return "None";
    const h = Math.floor(m / 60);
    const min = m % 60;
    if (h && !min) return `${h} hour${h > 1 ? "s" : ""}`;
    if (h && min) return `${h}h ${min}m`;
    return `${m} min`;
  };

  useEffect(() => {
    (async () => {
      try {
        const headers = buildHeaders(token);
        
        const res = await fetch(`${API}/service/booking-time/`, {
          headers,
        });
        const data = await res.json();

        if (data?.duration !== undefined) {
          const d = String(data.duration);
          if (durationOptions.some((o) => o.value === d)) {
            setDuration(d);
          } else {
            setDuration("Other");
            setCustomDuration(d);
          }
        }

        if (data?.response_time) {
          setResponseTime(data.response_time);
        }

        if (data?.tone_of_voice_id) {
          setSelectedTone(data.tone_of_voice_id);
        }
      } catch {
        console.log("No saved booking-time settings yet.");
      }
    })();
  }, [token]);

  const save = async () => {
    const finalDuration =
      duration === "Other" ? Number(customDuration || 0) : Number(duration);

    const payload = {
      duration: finalDuration,
      time_unit: "minutes",
      response_time: responseTime,
      tone_of_voice_id: selectedTone,
    };

    setIsSaving(true);
    try {
      const headers = buildHeaders(token);
      
      const res = await fetch(`${API}/service/booking-time/`, {
        method: "PUT",
        headers,
        body: JSON.stringify(payload),
      });

      if (!res.ok) throw new Error();

      toast.success("Settings saved successfully!", {
        icon: "✅",
        style: {
          background: "#10B981",
          color: "white",
        },
      });
    } catch {
      toast.error("Error saving settings");
    } finally {
      setIsSaving(false);
    }
  };

  if (!choices)
    return (
      <div className="animate-pulse bg-white rounded-xl p-6 shadow-sm border">
        <div className="h-6 bg-gray-200 rounded w-48 mb-4"></div>
        <div className="h-4 bg-gray-200 rounded w-full mb-2"></div>
      </div>
    );

  return (
    <motion.div
      className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <div className="flex items-center gap-3 mb-6">
        <div className="p-2 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg">
          <Clock className="w-5 h-5 text-white" />
        </div>
        <div>
          <h2 className="text-xl font-normal text-gray-800">
            Booking Settings
          </h2>
        </div>
      </div>

      <div className="space-y-8">
        <div className="grid md:grid-cols-2 gap-8">
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-blue-500" />
              <h3 className="font-semibold text-gray-700">Buffer Time</h3>
            </div>
            <p className="text-sm text-gray-600">
              Set buffer time between appointments
            </p>

            <div className="relative">
              <div className="relative">
                <button
                  onClick={() => setOpenDropdown(!openDropdown)}
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl 
                           flex items-center justify-between hover:border-blue-300 
                           transition-colors group"
                >
                  <div className="flex items-center gap-3">
                    <span className="text-blue-500">
                      {durationOptions.find((d) => d.value === duration)?.icon}
                    </span>
                    <span className="font-medium text-gray-700">
                      {duration === "Other"
                        ? customDuration
                          ? formatDuration(customDuration)
                          : "Custom duration"
                        : formatDuration(duration)}
                    </span>
                  </div>
                  <motion.div
                    animate={{ rotate: openDropdown ? 180 : 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <ChevronDown className="w-4 h-4 text-gray-400 group-hover:text-blue-500" />
                  </motion.div>
                </button>

                <AnimatePresence>
                  {openDropdown && (
                    <motion.div
                      className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-200 
                               rounded-xl shadow-lg z-10 overflow-hidden"
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      transition={{ duration: 0.2 }}
                    >
                      {durationOptions.map((option) => (
                        <motion.button
                          key={option.value}
                          whileHover={{ backgroundColor: "#f8fafc" }}
                          onClick={() => {
                            setDuration(option.value);
                            if (option.value !== "Other") setCustomDuration("");
                            setOpenDropdown(false);
                          }}
                          className={`w-full px-4 py-3 flex items-center justify-between 
                                   transition-colors ${
                                     duration === option.value
                                       ? "bg-blue-50"
                                       : ""
                                   }`}
                        >
                          <div className="flex items-center gap-3">
                            <span
                              className={`${
                                duration === option.value
                                  ? "text-blue-500"
                                  : "text-gray-500"
                              }`}
                            >
                              {option.icon}
                            </span>
                            <span className="font-medium">{option.label}</span>
                          </div>
                          {duration === option.value && (
                            <Check className="w-4 h-4 text-blue-600" />
                          )}
                        </motion.button>
                      ))}

                      {duration === "Other" && (
                        <div className="p-4 border-t border-gray-100">
                          <div className="flex items-center gap-2">
                            <Clock className="w-4 h-4 text-gray-400" />
                            <input
                              type="number"
                              min="0"
                              placeholder="Enter minutes..."
                              value={customDuration}
                              onChange={(e) =>
                                setCustomDuration(e.target.value)
                              }
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg 
                                       focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                          </div>
                        </div>
                      )}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Zap className="w-4 h-4 text-green-500" />
              <h3 className="font-semibold text-gray-700">Response Time</h3>
            </div>
            <p className="text-sm text-gray-600">
              When Halper will respond to clients
            </p>

            <div className="space-y-3">
              {choices.response_time_choices?.map((item, i) => (
                <motion.label
                  key={i}
                  whileHover={{ scale: 1.01 }}
                  className={`flex items-center justify-between p-4 rounded-xl border cursor-pointer 
                           transition-all ${
                             responseTime === item.value
                               ? "border-green-500 bg-green-50"
                               : "border-gray-200 hover:border-gray-300"
                           }`}
                >
                  <div className="flex items-center gap-3">
                    <MessageSquare className="w-4 h-4 text-gray-400" />
                    <span className="font-medium text-gray-700">
                      {item.value}
                    </span>
                  </div>
                  <div className="relative">
                    <input
                      type="radio"
                      name="responseTime"
                      checked={responseTime === item.value}
                      onChange={() => setResponseTime(item.value)}
                      className="sr-only"
                    />
                    <div
                      className={`w-5 h-5 rounded-full border-2 flex items-center justify-center
                                  ${
                                    responseTime === item.value
                                      ? "border-green-500 bg-green-500"
                                      : "border-gray-300"
                                  }`}
                    >
                      {responseTime === item.value && (
                        <div className="w-2 h-2 bg-white rounded-full" />
                      )}
                    </div>
                  </div>
                </motion.label>
              ))}
            </div>
          </div>
        </div>

        <div className="pt-4 border-t border-gray-100">
          <div className="flex items-center gap-2 mb-6">
            <Mic className="w-5 h-5 text-[#6366F1]" />
            <h3 className="font-semibold text-gray-700">Tone of Voice</h3>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {choices.tone_of_voice_options?.map((tone) => (
              <motion.div
                key={tone.id}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => setSelectedTone(tone.id)}
                className={`cursor-pointer rounded-xl border-2 p-5 transition-all
           ${
             selectedTone === tone.id
               ? "border-[#6366F1] bg-[#EEF2FF]"
               : "border-gray-200 hover:border-gray-300"
           }`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div
                      className={`p-2 rounded-lg ${
                        selectedTone === tone.id
                          ? "bg-gray-100"
                          : "bg-gray-100"
                      }`}
                    >
                      <MessageSquare
                        className={`w-4 h-4 ${
                          selectedTone === tone.id
                            ? "text-[#6366F1]"
                            : "text-gray-600"
                        }`}
                      />
                    </div>
                    <h4 className="font-semibold text-gray-800">
                      {tone.title}
                    </h4>
                  </div>
                  <div
                    className={`w-6 h-6 rounded-full flex items-center justify-center
                                ${
                                  selectedTone === tone.id
                                    ? "bg-[#6366F1]"
                                    : "bg-gray-100"
                                }`}
                  >
                    {selectedTone === tone.id && (
                      <Check className="w-3 h-3 text-white" />
                    )}
                  </div>
                </div>
                <p className="text-sm text-gray-600 leading-relaxed">
                  {tone.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>

        <motion.button
          onClick={save}
          disabled={isSaving}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          className="w-full md:w-auto px-8 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 
                   text-white font-semibold rounded-xl shadow-md hover:shadow-lg 
                   transition-all flex items-center justify-center gap-3
                   disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSaving ? (
            <>
              <div
                className="w-4 h-4 border-2 border-white border-t-transparent 
                            rounded-full animate-spin"
              />
              Saving...
            </>
          ) : (
            <>
              <Save className="w-4 h-4" />
              Save Settings
            </>
          )}
        </motion.button>
      </div>
    </motion.div>
  );
}
